/**
 * Generic envelope wrapping every response from the live KSS-Mart backend.
 *
 * Every endpoint at `https://api.knottique.kristysoftware.com` returns this
 * shape (verified against the swagger `ObjectResponse` schema). Successful
 * calls have `success: true` and the typed payload in `data`. Failures set
 * `success: false` and put a human-readable reason in `message`.
 */
export interface ObjectResponse<T = unknown> {
  success: boolean;
  message?: string | null;
  data: T | null;
}

export interface UnwrapOptions {
  /**
   * When true, a `data: null` body is treated as success and returns
   * `undefined` instead of throwing. Use for endpoints that return no
   * meaningful payload — `delete`, `revoke`, etc.
   */
  allowNullData?: boolean;
}

/**
 * Strip the `ObjectResponse` envelope and return the inner data, or throw
 * a descriptive `Error` on failure / empty payload.
 *
 * Throws when the body is missing, when `success` is false, or when `data`
 * is null and `allowNullData` was not set.
 */
export function unwrap<T>(env: ObjectResponse<T> | undefined | null, opts: UnwrapOptions = {}): T {
  if (!env || env.success === false) {
    throw new Error(env?.message || 'Request failed');
  }
  if (env.data == null) {
    if (opts.allowNullData) return undefined as unknown as T;
    throw new Error(env.message || 'Empty response payload');
  }
  return env.data;
}

/**
 * Defensive list-unwrap. The swagger spec doesn't pin whether entity
 * collection endpoints respond with `data: T[]` or `data: { items: T[] }`,
 * so this helper accepts either shape and always returns a plain array.
 *
 * If `data` is neither shape (e.g. an envelope-y object without `items`),
 * returns an empty array rather than throwing — list pages handle empty
 * gracefully and a noisy throw here would block the whole grid render.
 */
export function unwrapItems<T>(env: ObjectResponse<T[] | { items?: T[] | null }> | undefined | null): T[] {
  if (!env || env.success === false) {
    throw new Error(env?.message || 'Request failed');
  }
  const d = env.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray((d as { items?: T[] }).items)) {
    return (d as { items: T[] }).items;
  }
  return [];
}
